import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = auth
  const [error, setError] = useState("");

  useEffect(() => {
    // Check current session once on mount.
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const register = async (name, email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      return { ok: true, needsVerification: data.needs_verification, message: data.message, email };
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return { ok: false };
    }
  };

  const resendVerification = async (email) => {
    try {
      await api.post("/auth/resend-verification", { email });
      return true;
    } catch {
      return false;
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  const value = useMemo(
    () => ({ user, error, setError, login, register, logout, resendVerification }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, error]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
